import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePageRestore } from '../../../lib/browser/usePageRestore';
import { isEntraLaptopMode } from '../../../lib/config/entraLaptopMode';
import { usePermission } from '../../../ui';
import { stubConnect } from '../mailboxApi';
import { type RefetchConnection, useMailboxConnection } from '../useMailboxConnection';
import { readConnectMarker, withoutConnectMarkers } from './connectMarkers';
import { connectReturnPath } from './connectReturnPath';
import {
    MailboxConnectContext,
    type MailboxConnectFailure,
    type MailboxConnectValue,
} from './mailboxConnectContext';
import { startMailboxConnect } from './startMailboxConnect';

/** The Email screen's arm: the settled-check count it was set at. */
interface MountArm {
    readonly after: number;
}

/**
 * The one owner of the mailbox connect in the CRM (F20-i spec FR-16, FR-17, FR-19; WI-1.3 plan D-4–D-12,
 * D-26). Mounted once in `src/app/router.tsx`, around the app frame and inside the authenticated gate's
 * permission provider, so it lives as long as the page does.
 *
 * - **The check.** For a user whose permissions include `email.manage` it observes the one connection query
 *   (`useMailboxConnection`, key `['mailbox','connection']`) that the Email screen and the mailbox screen
 *   also observe, so the check runs on every CRM load and again each time either screen mounts by in-app
 *   navigation. For any other user the query is disabled and never sent; when the session's `/me` re-read
 *   (on window focus or visibility) adds `email.manage`, the query is enabled and read at once (F20-i AC-60).
 * - **When the automatic connect may start** (the Wave-1 contract's "When the client calls the check"; the
 *   lead's decision of 2026-11-02 in the F20-i WI-1.3 plan's `## Notes`, which replaced D-21). Only on a
 *   `CONNECT_NOW` from **the load's first settled check** (for a user who holds `email.manage` when the page
 *   loads) or from **the check the Email screen's mount reads** (`armAutomaticConnect`). A `CONNECT_NOW`
 *   from any other re-read — the one a mid-session grant of `email.manage` starts, above all — starts
 *   nothing and spends nothing: sending the user to Microsoft from whatever page they are on would lose what
 *   they had typed there, so the next load or the Email page starts it. The page's F20-i FR-16 button then
 *   offers *Try again* rather than a "Connecting" line that nothing would end (`automaticPending`).
 * - **The automatic connect.** It starts **once per page load** (a ref), and never on a load that arrived
 *   carrying a marker — a client guard beside the server's per-session memory, so a backend fault cannot loop
 *   the browser through Microsoft (F20-i WI-1.3 plan D-7). Under
 *   `pnpm dev` and Vitest it calls the stub connect with `mode: AUTOMATIC` and stays on the page; in a
 *   deployed bundle, and in laptop mode, it navigates the whole page with `mode=automatic` (F20-i WI-1.3 plan D-6).
 * - **The markers.** `mailboxConnect` and `mailboxConnectReason` are consumed into state and removed from
 *   the URL with `replace`, so no history entry is added and a reload does not show them again.
 * - **A back/forward-cache restore** re-reads the check and clears the failure on show (L-109).
 * - **One failure, one place.** A stub `409`, a `failed` marker, or any other failure of the automatic stub
 *   connect (a `500`, a network failure — F20-i WI-1.3 plan D-26) is recorded here and shown by `MailboxConnectBanner`; then
 *   the check is re-read. The interactive starts of every button go through `startInteractive`, so their
 *   `409` lands here too (F20-i WI-1.3 plan D-12).
 *
 * The `import.meta.env.DEV` literal stays first in each stub condition: it is what lets Vite drop the stub
 * branch, and `stubConnect` with it, from a production bundle (F20-a WI-1.2 plan Decision 7).
 */
export function MailboxConnectProvider({ children }: { readonly children: ReactNode }) {
    const allowed = usePermission('email.manage').kind === 'allowed';
    const query = useMailboxConnection({ enabled: allowed });
    const [searchParams, setSearchParams] = useSearchParams();
    const marker = readConnectMarker(searchParams);

    const [failure, setFailureState] = useState<MailboxConnectFailure | null>(null);
    // The banner's status line is held empty (`MailboxConnectValue.statusQuiet`) until the failure changes.
    const [statusQuiet, setStatusQuiet] = useState(false);
    /** Record the failure on show (or none); a new one is announced, so the status line speaks again. */
    const setFailure = useCallback((next: MailboxConnectFailure | null) => {
        setFailureState(next);
        setStatusQuiet(false);
    }, []);
    const [automaticSpent, setAutomaticSpent] = useState(false);
    const [automaticRunning, setAutomaticRunning] = useState(false);
    // The two arms, each letting one settled check start the automatic connect. The load's: its first settled
    // check, for a key holder at load. The Email screen's: the first check that settles after its mount, while
    // it is still mounted (its own object, so only its unmount clears it).
    const [loadArmed, setLoadArmed] = useState(allowed);
    const [mountArm, setMountArm] = useState<MountArm | null>(null);
    const [nextStepButtons, setNextStepButtons] = useState(0);
    // Read in the first render only: whether this page load arrived carrying a marker (F20-i WI-1.3 plan D-7).
    const [arrivedWithMarker] = useState(() => marker !== undefined);
    // The one automatic start of this page load.
    const automaticStarted = useRef(false);

    // The latest re-read, so the callbacks below stay stable while still reading through the current query.
    const refetchRef = useRef<RefetchConnection>(query.refetch);
    refetchRef.current = query.refetch;
    const settledCountRef = useRef(query.settledCount);
    settledCountRef.current = query.settledCount;
    const allowedRef = useRef(allowed);
    allowedRef.current = allowed;

    /** Re-read the check, through the caller's re-read when it passed one. Only for a key holder. */
    const reread = useCallback(async (refetch?: RefetchConnection): Promise<void> => {
        if (!allowedRef.current) {
            return;
        }
        await (refetch ?? refetchRef.current)();
    }, []);

    // Consume a marker into state, then remove it from the URL without a history entry. Only a value this
    // client recognises is consumed: an unknown one is left as it came.
    // Keyed on plain values, not the marker object, which is new on every render.
    const markerKind = marker?.kind;
    const markerReason = marker?.kind === 'failed' ? marker.reason : null;
    useEffect(() => {
        if (markerKind === undefined) {
            return;
        }
        if (markerKind === 'failed') {
            setFailure({ reason: markerReason });
        } else {
            setFailure(null);
            void reread();
        }
        // Built from this render's params, not the URL at the moment it runs (React Router's functional form
        // reads the same render-time params). It assumes no other effect in the same commit changes the
        // query string first: a child's `setSearchParams` running before this one would be overwritten. The
        // marker arrives only on the load that returns from Microsoft, before any page has written to the
        // URL; once the marker is gone this effect returns at once, so it never writes over a later change.
        setSearchParams(withoutConnectMarkers(searchParams), { replace: true });
    }, [markerKind, markerReason, searchParams, setSearchParams, reread, setFailure]);

    const nextStep = query.state === 'connected' || query.state === 'not-connected' ? query.nextStep : null;

    const { settledCount } = query;

    useEffect(() => {
        if (!allowed) {
            // Without `email.manage` no check may start it, and an arm set before the key went must not fire on
            // the check a later re-grant reads: that check is neither the load's first nor the Email mount's.
            setLoadArmed(false);
            setMountArm(null);
            return;
        }
        const loadDue = loadArmed && settledCount > 0;
        const mountDue = mountArm !== null && settledCount > mountArm.after;
        if (!(loadDue || mountDue)) {
            return;
        }
        // The check an arm waited for has settled: that arm is used, whatever the check said.
        if (loadDue) {
            setLoadArmed(false);
        }
        if (mountDue) {
            setMountArm(null);
        }
        if (nextStep !== 'CONNECT_NOW' || automaticStarted.current) {
            return;
        }
        automaticStarted.current = true;
        if (arrivedWithMarker) {
            // The load came back from the connect flow: never start another one on it (F20-i WI-1.3 plan D-7).
            setAutomaticSpent(true);
            return;
        }
        if (import.meta.env.DEV && !isEntraLaptopMode()) {
            setAutomaticRunning(true);
            void (async () => {
                try {
                    const result = await stubConnect('AUTOMATIC');
                    setFailure(result.kind === 'failed' ? { reason: result.reason } : null);
                } catch {
                    // Any other failure of the automatic start still shows the message and *Try again*,
                    // so a `CONNECT_NOW` never leaves the user with nothing to do (F20-i WI-1.3 plan D-26).
                    setFailure({ reason: null });
                }
                setAutomaticSpent(true);
                setAutomaticRunning(false);
                await reread();
            })();
            return;
        }
        startMailboxConnect({ automatic: true, returnTo: connectReturnPath() });
        setAutomaticSpent(true);
    }, [allowed, loadArmed, mountArm, settledCount, nextStep, arrivedWithMarker, reread, setFailure]);

    /**
     * The Email screen has mounted: the check its mount reads may start the automatic connect. Returns the
     * call that clears this arm, and only this one, for the screen's unmount: a check that settles once the
     * user has left the Email page must not send them to Microsoft from wherever they are now.
     */
    const armAutomaticConnect = useCallback(() => {
        const arm: MountArm = { after: settledCountRef.current };
        setMountArm(arm);
        return () => setMountArm((current) => (current === arm ? null : current));
    }, []);

    // Whether a `CONNECT_NOW` on screen is about to be acted on, or is being: an arm waits for its check, or
    // the stub start runs. Otherwise the page's F20-i FR-16 button offers *Try again*.
    const automaticPending =
        !automaticSpent && (automaticRunning || (allowed && (loadArmed || mountArm !== null)));

    const startInteractive = useCallback(
        async (refetch?: RefetchConnection): Promise<void> => {
            if (import.meta.env.DEV && !isEntraLaptopMode()) {
                // The failure on show stays while the start runs, so the banner, and the button that has
                // focus, stay too (`.forge/lessons.md` L-133); `statusQuiet` empties the banner's status line,
                // so a second failure fills it again and is announced again.
                setStatusQuiet(true);
                // Any refusal but the forced `409` rejects here: the connect never started, so the failure on
                // show is still true, and the rejection goes to the caller, whose own panel says the connect
                // could not start, with its trace id. The status line stays empty until the failure changes,
                // so the old failure is not read out again beside that panel's alert.
                const result = await stubConnect();
                setFailure(result.kind === 'failed' ? { reason: result.reason } : null);
                await reread(refetch);
                return;
            }
            // The failure stays: the whole page navigates away, and the busy *Try again* stays until it
            // unloads (`.forge/lessons.md` L-098).
            startMailboxConnect({ automatic: false, returnTo: connectReturnPath() });
        },
        [reread, setFailure],
    );

    const dismissFailure = useCallback(() => setFailure(null), [setFailure]);

    /*
     * A back/forward-cache restore brings the page back with its state as it was frozen (BUG-066,
     * `.forge/lessons.md` L-109): a failure taken from a marker already stripped from the URL would show again
     * after the user left the CRM by a full-page navigation and pressed Back. The failure described a moment
     * the user has moved past, so the check is re-read (for a key holder) and the failure cleared once the
     * re-read has settled. Any arm is cleared too: it was frozen with the page, and the re-read is not the
     * check the load or the Email screen's mount was waiting for.
     */
    usePageRestore(() => {
        setLoadArmed(false);
        setMountArm(null);
        void (async () => {
            await reread();
            setFailure(null);
        })();
    });

    const registerNextStepButton = useCallback(() => {
        setNextStepButtons((count) => count + 1);
        return () => setNextStepButtons((count) => count - 1);
    }, []);

    const value = useMemo<MailboxConnectValue>(
        () => ({
            failure,
            statusQuiet,
            automaticPending,
            armAutomaticConnect,
            startInteractive,
            dismissFailure,
            registerNextStepButton,
            nextStepButtonShown: nextStepButtons > 0,
        }),
        [
            failure,
            statusQuiet,
            automaticPending,
            armAutomaticConnect,
            startInteractive,
            dismissFailure,
            registerNextStepButton,
            nextStepButtons,
        ],
    );

    return <MailboxConnectContext.Provider value={value}>{children}</MailboxConnectContext.Provider>;
}
